import { useLanguage } from '../../contexts/LanguageContext';
import { useInvoiceTheme, invoiceThemes } from '../../hooks/use-invoice-theme';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Palette } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function InvoiceThemeSettings() {
  const { theme, setTheme } = useInvoiceTheme();
  const { t } = useLanguage();

  const themes = [
    { id: 'theme1' as const, name: t('settings.invoiceTheme.theme1'), description: t('settings.invoiceTheme.theme1Desc') },
    { id: 'theme2' as const, name: t('settings.invoiceTheme.theme2'), description: t('settings.invoiceTheme.theme2Desc') },
    { id: 'theme3' as const, name: t('settings.invoiceTheme.theme3'), description: t('settings.invoiceTheme.theme3Desc') },
    { id: 'theme4' as const, name: t('settings.invoiceTheme.theme4'), description: t('settings.invoiceTheme.theme4Desc') },
    { id: 'theme5' as const, name: t('settings.invoiceTheme.theme5'), description: t('settings.invoiceTheme.theme5Desc') },
    { id: 'theme6' as const, name: t('settings.invoiceTheme.theme6'), description: t('settings.invoiceTheme.theme6Desc') },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/settings">
              <Button variant="outline" size="sm" className="border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('settings.back')}
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">{t('settings.invoiceTheme.title')}</h1>
              <p className="text-slate-500 text-sm mt-0.5">{t('settings.invoiceTheme.subtitle')}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              {t('settings.invoiceTheme.selectTheme')}
            </CardTitle>
            <CardDescription>
              {t('settings.invoiceTheme.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {themes.map((themeOption) => {
                const themeColors = invoiceThemes[themeOption.id];
                const isSelected = theme === themeOption.id;
                
                return (
                  <div
                    key={themeOption.id}
                    className={`border-2 rounded-lg overflow-hidden transition-all cursor-pointer ${
                      isSelected
                        ? 'border-slate-900 shadow-lg'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => setTheme(themeOption.id)}
                  >
                    {/* Preview Header */}
                    <div
                      className="h-16 w-full flex items-center justify-center"
                      style={{ backgroundColor: themeColors.headerBg }}
                    >
                      <span className="text-white font-bold text-sm">پێشانگای دارا</span>
                    </div>
                    
                    {/* Preview Content */}
                    <div className="p-4 bg-white">
                      <div className="space-y-2">
                        <div
                          className="h-4 rounded"
                          style={{ backgroundColor: themeColors.accentBg, width: '60%' }}
                        />
                        <div
                          className="h-3 rounded"
                          style={{ backgroundColor: themeColors.accentColor, width: '40%' }}
                        />
                        <div className="flex gap-2 mt-3">
                          <div
                            className="h-6 rounded flex-1"
                            style={{ backgroundColor: themeColors.headerBg }}
                          />
                          <div
                            className="h-6 rounded w-12"
                            style={{ backgroundColor: themeColors.accentColor }}
                          />
                        </div>
                      </div>
                      
                      <div className="mt-4">
                        <h3 className="font-semibold text-sm mb-1">{themeOption.name}</h3>
                        <p className="text-xs text-slate-600">{themeOption.description}</p>
                      </div>
                    </div>
                    
                    {isSelected && (
                      <div className="bg-slate-900 text-white text-center py-2">
                        <span className="text-sm font-medium">{t('settings.invoiceTheme.selected')}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-100">
              <h3 className="font-medium text-slate-800 mb-2">{t('settings.invoiceTheme.currentTheme')}</h3>
              <p className="text-slate-600">
                {themes.find(th => th.id === theme)?.name}
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

